import {useUserContext} from '@/components/Providers/UserContext';
import useAuthenticatedFetch from '@/hooks/useAuthenticatedFetch.ts';
import type {DirectoryStudent} from '@/types/DirectoryStudent';
import {Alert, Box, Button, Grid2, Modal, Skeleton, useMediaQuery, useTheme} from '@mui/material';
import Typography from '@mui/material/Typography';
import type {ReactElement, ReactNode} from 'react';
import {useEffect, useState} from 'react';

type Props = {
    open: boolean;
    handleClose: () => void;
    directoryStudent: DirectoryStudent | null;
};

type StudentDetailsType = {
    address: string;
    address2: string | null;
    city: string;
    state: string;
    zip: string;
    schoolName: string;
    phoneNumber: string;
};

const RowElement = ({title, value}: {title: string; value: string | ReactNode}) => {
    return (
        <Grid2 container sx={{mt: 1}}>
            <Grid2
                size={{
                    xs: 12,
                    md: 4,
                }}
            >
                <Typography variant='subtitle2'>{title}</Typography>
            </Grid2>
            <Grid2
                size={{
                    xs: 12,
                    md: 8,
                }}
            >
                <Typography>{value}</Typography>
            </Grid2>
        </Grid2>
    );
};

const StudentModal = ({open, handleClose, directoryStudent}: Props): ReactElement => {
    const {user} = useUserContext();
    const fetch = useAuthenticatedFetch();
    const [student, setStudent] = useState<StudentDetailsType | null>(null);
    const [error, setError] = useState<null | string>(null);
    const theme = useTheme();
    const isMdOrSmaller = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        setStudent(null);
        setError(null);

        if (!(directoryStudent && user)) {
            return;
        }

        const apiCall = async () => {
            const url = new URL(
                `/v1/directory/student/${directoryStudent.studentId}`,
                import.meta.env.VITE_APP_API_ENDPOINT,
            );

            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const json = (await response.json()) as StudentDetailsType;
                setStudent(json);
            } else {
                setError('There was an error retrieving the student.');
            }
        };

        apiCall().catch(e => {
            console.error('error Loading', e);
            setError('There was an error retrieving the student.');
        });
    }, [directoryStudent, fetch, user]);

    if (!directoryStudent) {
        return <></>;
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby='modal-modal-title'
            aria-describedby='modal-modal-description'
        >
            <Box
                sx={[
                    {
                        position: 'absolute' as const,
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                    },
                    isMdOrSmaller
                        ? {
                              width: '100%',
                          }
                        : {
                              width: '800px',
                          },
                ]}
            >
                <Grid2 container spacing={2}>
                    <Grid2
                        size={{
                            xs: 12,
                            md: 6,
                        }}
                    >
                        <Typography variant='h6' component='h2'>
                            {directoryStudent.nameFirst} {directoryStudent.nameLast}
                        </Typography>
                        <RowElement
                            title='School'
                            value={
                                student ? (
                                    student.schoolName
                                ) : (
                                    <Skeleton variant='text' sx={{fontSize: '1rem'}} />
                                )
                            }
                        />
                        <RowElement title='Grade' value={`${directoryStudent.grade}`} />
                        <RowElement
                            title='Phone Number'
                            value={
                                student ? (
                                    student.phoneNumber
                                ) : (
                                    <Skeleton variant='text' sx={{fontSize: '1rem'}} />
                                )
                            }
                        />
                        <RowElement
                            title='Email'
                            value={`${directoryStudent.studentId}@glenbrook225.org`}
                        />
                        <Grid2 container sx={{mt: 1}}>
                            <Grid2
                                size={{
                                    xs: 12,
                                    md: 4,
                                }}
                            >
                                <Typography variant='subtitle2'>Address</Typography>
                            </Grid2>
                        </Grid2>
                        <Grid2 container>
                            <Grid2 size={12}>
                                <Typography>
                                    {student ? (
                                        `${student.address} ${student.address2 ?? ''}`
                                    ) : error ? (
                                        <></>
                                    ) : (
                                        <Skeleton variant='text' sx={{fontSize: '1rem'}} />
                                    )}
                                </Typography>
                            </Grid2>
                        </Grid2>
                        <Grid2 container>
                            <Grid2 size={12}>
                                <Typography>
                                    {student ? (
                                        `${student.city}, ${student.state} ${student.zip}`
                                    ) : error ? (
                                        <></>
                                    ) : (
                                        <Skeleton variant='text' sx={{fontSize: '1rem'}} />
                                    )}
                                </Typography>
                            </Grid2>
                        </Grid2>
                    </Grid2>
                    <Grid2
                        container
                        direction='row'
                        justifyContent={isMdOrSmaller ? 'center' : 'flex-end'}
                        alignItems='center'
                        size={{
                            xs: 12,
                            md: 6,
                        }}
                    >
                        {error && <Alert severity='error'>{error}</Alert>}
                    </Grid2>
                </Grid2>
                <Button sx={{mt: 1}} onClick={handleClose}>
                    Close
                </Button>
            </Box>
        </Modal>
    );
};

export default StudentModal;
